import { CommonModule } from '@angular/common';
import { Component, Inject, inject, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TextMaskModule } from 'angular2-text-mask';
import { MatSpinnerOverlayComponent } from 'src/app/components/mat-spinner-overlay/mat-spinner-overlay.component';
import { SnackBarService } from '../../services/snack-bar-service.service';
import { HabilitacaoSwapService } from '../services/habilitacao-swap.service';
import { SocioModel, TipoDocumento } from '../models';

export interface CadastrarSocioDialogData {
  socio?: SocioModel;
  cpfsCnpjsExistentes: string[];
}

@Component({
  selector: 'app-cadastrar-socio',
  templateUrl: './cadastrar-socio.component.html',
  styleUrls: ['./cadastrar-socio.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSpinnerOverlayComponent,
    TextMaskModule,
  ],
})
export class CadastrarSocioComponent implements OnInit {
  // ==================== Dependencies ====================
  public loading = false;
  public dialogRef = inject(MatDialogRef<CadastrarSocioComponent>);
  private service = inject(HabilitacaoSwapService);
  private snack = inject(SnackBarService);

  // ==================== State ====================
  public isEditMode = false;
  public tipoDocumento: TipoDocumento = 'CPF';

  // ==================== Masks ====================
  readonly cpfMask = [/\d/, /\d/, /\d/, '.', /\d/, /\d/, /\d/, '.', /\d/, /\d/, /\d/, '-', /\d/, /\d/];
  // CNPJ alfanumérico: aceita letras e números nas 8 primeiras posições
  readonly cnpjMask = [/[A-Za-z0-9]/, /[A-Za-z0-9]/, '.', /[A-Za-z0-9]/, /[A-Za-z0-9]/, /[A-Za-z0-9]/, '.', /[A-Za-z0-9]/, /[A-Za-z0-9]/, /[A-Za-z0-9]/, '/', /[A-Za-z0-9]/, /[A-Za-z0-9]/, /[A-Za-z0-9]/, /[A-Za-z0-9]/, '-', /\d/, /\d/];

  get documentoMask(): (RegExp | string)[] {
    return this.isCpf ? this.cpfMask : this.cnpjMask;
  }

  // ==================== Forms ====================
  socioForm = new FormGroup({
    nome: new FormControl('', [Validators.required, Validators.maxLength(200)]),
    tipoDocumento: new FormControl<TipoDocumento>('CPF', [Validators.required]),
    cpfCnpj: new FormControl('', [Validators.required]),
    dataNascimento: new FormControl<Date | null>(null),
    nomePai: new FormControl(''),
    nomeMae: new FormControl(''),
    razaoSocial: new FormControl(''),
    nomeReduzido: new FormControl(''),
    dataFundacao: new FormControl<Date | null>(null),
  });

  constructor(@Inject(MAT_DIALOG_DATA) public data: CadastrarSocioDialogData) {}

  ngOnInit(): void {
    if (this.data?.socio) {
      this.isEditMode = true;
      this.tipoDocumento = this.data.socio.tipoDocumento;
      this.socioForm.patchValue({
        nome: this.data.socio.nome,
        tipoDocumento: this.data.socio.tipoDocumento,
        cpfCnpj: this.data.socio.cpfCnpj,
        dataNascimento: this.data.socio.dataNascimento ? new Date(this.data.socio.dataNascimento) : null,
        nomePai: this.data.socio.nomePai,
        nomeMae: this.data.socio.nomeMae,
        razaoSocial: this.data.socio.razaoSocial,
        nomeReduzido: this.data.socio.nomeReduzido,
        dataFundacao: this.data.socio.dataFundacao ? new Date(this.data.socio.dataFundacao) : null,
      });
    }
    this.onTipoDocumentoChange();

    this.socioForm.get('tipoDocumento')?.valueChanges.subscribe((tipo) => {
      if (tipo) {
        this.tipoDocumento = tipo;
        this.socioForm.get('cpfCnpj')?.setValue('');
        this.onTipoDocumentoChange();
      }
    });
  }

  // ==================== Getters ====================
  get isCpf(): boolean {
    return this.tipoDocumento === 'CPF';
  }

  // ==================== Validators Toggle ====================
  private onTipoDocumentoChange(): void {
    const dataNascimento = this.socioForm.get('dataNascimento');
    const nomePai = this.socioForm.get('nomePai');
    const nomeMae = this.socioForm.get('nomeMae');
    const razaoSocial = this.socioForm.get('razaoSocial');
    const nomeReduzido = this.socioForm.get('nomeReduzido');
    const dataFundacao = this.socioForm.get('dataFundacao');

    if (this.isCpf) {
      dataNascimento?.setValidators([Validators.required]);
      nomePai?.setValidators([Validators.required, Validators.maxLength(200)]);
      nomeMae?.setValidators([Validators.required, Validators.maxLength(200)]);
      razaoSocial?.clearValidators();
      nomeReduzido?.clearValidators();
      dataFundacao?.clearValidators();
      razaoSocial?.setValue('');
      nomeReduzido?.setValue('');
      dataFundacao?.setValue(null);
    } else {
      dataNascimento?.clearValidators();
      nomePai?.clearValidators();
      nomeMae?.clearValidators();
      dataNascimento?.setValue(null);
      nomePai?.setValue('');
      nomeMae?.setValue('');
      razaoSocial?.setValidators([Validators.maxLength(200)]);
      nomeReduzido?.setValidators([Validators.maxLength(200)]);
      dataFundacao?.clearValidators();
    }

    dataNascimento?.updateValueAndValidity();
    nomePai?.updateValueAndValidity();
    nomeMae?.updateValueAndValidity();
    razaoSocial?.updateValueAndValidity();
    nomeReduzido?.updateValueAndValidity();
    dataFundacao?.updateValueAndValidity();
  }

  // ==================== Actions ====================
  onCancelar(): void {
    this.dialogRef.close(false);
  }

  onConfirmar(): void {
    if (!this.socioForm.valid) {
      this.socioForm.markAllAsTouched();
      return;
    }

    const rawDoc = (this.socioForm.value.cpfCnpj || '').replace(/[.\-\/]/g, '').toUpperCase();

    if (this.isCpf && !this.validarCpf(rawDoc)) {
      this.snack.flashMessage('CPF inválido', ['snackbar-error'], 'Fechar', 3000);
      return;
    }

    if (!this.isCpf && !this.validarCnpjAlfanumerico(rawDoc)) {
      this.snack.flashMessage('CNPJ inválido', ['snackbar-error'], 'Fechar', 3000);
      return;
    }

    const existentes = this.data?.cpfsCnpjsExistentes || [];
    const docEditando = this.data?.socio?.cpfCnpj?.replace(/[.\-\/]/g, '').toUpperCase();
    if (existentes.includes(rawDoc) && rawDoc !== docEditando) {
      this.snack.flashMessage(
        'Documento já cadastrado para outro sócio',
        ['snackbar-error'],
        'Fechar',
        3000
      );
      return;
    }

    this.loading = true;

    const dataNasc = this.socioForm.value.dataNascimento;
    const dataFund = this.socioForm.value.dataFundacao;

    const socio = new SocioModel(
      this.data?.socio?.id ?? null,
      this.socioForm.value.nome || '',
      rawDoc,
      this.tipoDocumento,
      dataNasc ? this.formatDate(dataNasc) : null,
      this.socioForm.value.nomePai || null,
      this.socioForm.value.nomeMae || null,
      this.socioForm.value.razaoSocial || null,
      this.socioForm.value.nomeReduzido || null,
      dataFund ? this.formatDate(dataFund) : null,
    );

    const request$ = this.isEditMode
      ? this.service.editarSocio(socio)
      : this.service.salvarSocio(socio);

    request$.subscribe({
      next: (response) => {
        this.loading = false;
        if (response?.sucesso) {
          this.dialogRef.close(response);
        }
      },
      error: () => {
        this.loading = false;
        this.snack.flashMessage(
          'Erro ao salvar sócio',
          ['snackbar-error'],
          'Fechar',
          3000
        );
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  // ==================== Utility ====================
  private formatDate(date: Date): string {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private validarCpf(cpf: string): boolean {
    if (!cpf || cpf.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(cpf)) return false;

    let soma = 0;
    for (let i = 0; i < 9; i++) soma += parseInt(cpf.charAt(i)) * (10 - i);
    let resto = (soma * 10) % 11;
    if (resto === 10) resto = 0;
    if (resto !== parseInt(cpf.charAt(9))) return false;

    soma = 0;
    for (let i = 0; i < 10; i++) soma += parseInt(cpf.charAt(i)) * (11 - i);
    resto = (soma * 10) % 11;
    if (resto === 10) resto = 0;
    return resto === parseInt(cpf.charAt(10));
  }

  private validarCnpjAlfanumerico(cnpj: string): boolean {
    if (!cnpj || cnpj.length !== 14) return false;
    if (/^(.)\1{13}$/.test(cnpj)) return false;
    return true;
  }
}
